import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, CATEGORIES, CATEGORY_LABELS, INCOME_CATEGORIES, PROPERTY_TYPES } from "@/lib/api";
import { useDemo } from "@/context/DemoContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Sparkle, CheckCircle, WarningCircle, FilePdf, Plus, Trash, MagicWand, ArrowCounterClockwise, Archive, FileArrowUp, ArrowSquareOut, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyCreate = { name: "", address: "", city: "", state: "", property_type: "single_family" };

export default function BillReview() {
  const { fmtMoney } = useDemo();
  const { id } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [props, setProps] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [vendor, setVendor] = useState("");
  const [stmtDate, setStmtDate] = useState("");
  const [total, setTotal] = useState(0);
  const [lines, setLines] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [createLineIdx, setCreateLineIdx] = useState(null);
  const [creating, setCreating] = useState(false);
  const [unitOpen, setUnitOpen] = useState(false);
  const [unitForm, setUnitForm] = useState({ property_id: "", label: "", monthly_rent: "" });
  const [unitLineIdx, setUnitLineIdx] = useState(null);
  const [creatingUnit, setCreatingUnit] = useState(false);

  const hydrate = (b) => {
    setBill(b);
    setVendor(b.vendor || "");
    setStmtDate(b.statement_date || "");
    setTotal(b.total_amount || 0);
    setLines(b.parsed_lines || []);
  };

  useEffect(() => {
    api.get(`/bills/${id}`).then((r) => hydrate(r.data)).catch(() => {});
    api.get("/properties").then((r) => setProps(r.data));
    let revoke;
    api.get(`/bills/${id}/pdf`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        revoke = url;
        setPdfUrl(url);
      })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [id]);

  const parse = async () => {
    setParsing(true);
    try {
      const res = await api.post(`/bills/${id}/parse`);
      hydrate(res.data);
      toast.success("Statement parsed by AI");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI parsing failed"); }
    finally { setParsing(false); }
  };

  const popOut = () => {
    if (!pdfUrl) { toast.error("Document still loading…"); return; }
    const w = Math.round(window.innerWidth * 0.5);
    const h = Math.round(window.innerHeight * 0.9);
    const left = window.screenX + window.innerWidth - w - 24;
    const top = window.screenY + 60;
    window.open(pdfUrl, "statement_popout", `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
  };
  const restage = async () => {    if (!window.confirm("Restage this bill? Its ledger entries will be removed and it returns to the inbox for re-processing.")) return;
    try {
      const res = await api.post(`/bills/${id}/reset`);
      toast.success(`Restaged — ${res.data.transactions_removed} ledger entr${res.data.transactions_removed === 1 ? "y" : "ies"} removed`);
      navigate("/bills-inbox");
    } catch (e) { toast.error("Failed to restage"); }
  };

  const updateLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { description: "", amount: 0, property_id: null, category: "other", type: "expense" }]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));
  const lineType = (l) => (l.type === "income" || l.type === "expense") ? l.type : (INCOME_CATEGORIES.has(l.category) ? "income" : "expense");
  const setCategory = (i, v) => updateLine(i, { category: v, type: INCOME_CATEGORIES.has(v) ? "income" : "expense" });
  const propUnits = (pid) => (props.find((p) => p.id === pid)?.units) || [];
  const normLabel = (v) => (v || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");
  const matchUnitId = (pid, guess) => {
    const ng = normLabel(guess);
    if (!ng) return null;
    const u = propUnits(pid).find((u) => {
      const nl = normLabel(u.label);
      return nl && (nl === ng || (ng.length >= 2 && (nl.includes(ng) || ng.includes(nl))));
    });
    return u ? u.id : null;
  };
  const setLineProperty = (i, pid) => updateLine(i, { property_id: pid, unit_id: pid ? matchUnitId(pid, lines[i].unit_guess) : null });

  const openAddUnit = (i) => {
    const l = lines[i];
    setUnitForm({ property_id: l.property_id || "", label: l.unit_guess || "", monthly_rent: "" });
    setUnitLineIdx(i);
    setUnitOpen(true);
  };

  const createUnitAndAttribute = async () => {
    if (!unitForm.property_id) { toast.error("Choose a property for this unit"); return; }
    if (!unitForm.label.trim()) { toast.error("Unit label is required"); return; }
    setCreatingUnit(true);
    try {
      const res = await api.post("/units", {
        property_id: unitForm.property_id,
        label: unitForm.label.trim(),
        monthly_rent: unitForm.monthly_rent ? Number(unitForm.monthly_rent) : 0,
      });
      const newUnit = res.data;
      const propsRes = await api.get("/properties");
      setProps(propsRes.data);
      const srcGuess = unitLineIdx != null ? lines[unitLineIdx]?.unit_guess : null;
      setLines((ls) => ls.map((l, idx) => {
        if (idx === unitLineIdx) return { ...l, property_id: unitForm.property_id, unit_id: newUnit.id };
        if (!l.unit_id && l.property_id === unitForm.property_id && srcGuess && l.unit_guess === srcGuess) return { ...l, unit_id: newUnit.id };
        return l;
      }));
      toast.success(`Added unit "${newUnit.label}" and attributed this line`);
      setUnitOpen(false); setUnitForm({ property_id: "", label: "", monthly_rent: "" }); setUnitLineIdx(null);
    } catch (e) { toast.error("Failed to add unit"); }
    finally { setCreatingUnit(false); }
  };

  const openCreateFromGuess = (i) => {
    const l = lines[i];
    setCreateForm({
      name: l.property_name_guess || l.address_guess || "",
      address: l.address_guess || "",
      city: l.city_guess || "",
      state: l.state_guess || "",
      property_type: "single_family",
    });
    setCreateLineIdx(i);
    setCreateOpen(true);
  };

  const createPropertyAndAttribute = async () => {
    if (!createForm.name.trim()) { toast.error("Property name is required"); return; }
    setCreating(true);
    try {
      const res = await api.post("/properties", { ...createForm });
      const newProp = res.data;
      const propsRes = await api.get("/properties");
      setProps(propsRes.data);
      // attribute the originating line + any other unattributed lines sharing the same guess
      const guess = createLineIdx != null ? lines[createLineIdx]?.property_name_guess : null;
      setLines((ls) => ls.map((l, idx) => {
        if (idx === createLineIdx) return { ...l, property_id: newProp.id };
        if (!l.property_id && guess && l.property_name_guess === guess) return { ...l, property_id: newProp.id };
        return l;
      }));
      toast.success(`Created "${newProp.name}" and attributed this line`);
      setCreateOpen(false);
      setCreateForm(emptyCreate);
      setCreateLineIdx(null);
    } catch (e) { toast.error("Failed to create property"); }
    finally { setCreating(false); }
  };

  const unattributed = lines.filter((l) => !l.property_id).length;
  const incomeTotal = lines.filter((l) => lineType(l) === "income").reduce((s, l) => s + Number(l.amount || 0), 0);
  const expenseTotal = lines.filter((l) => lineType(l) === "expense").reduce((s, l) => s + Number(l.amount || 0), 0);
  const isOwnerStmt = bill?.statement_type === "owner_statement" || incomeTotal > 0;

  const approve = async () => {
    if (lines.some((l) => !l.property_id)) {
      toast.error("Attribute every line to a property before approving");
      return;
    }
    setApproving(true);
    try {
      await api.post(`/bills/${id}/approve`, {
        vendor, statement_date: stmtDate || null, total_amount: Number(total),
        lines: lines.map((l) => ({ ...l, amount: Number(l.amount) })),
      });
      toast.success("Bill posted to ledger");
      navigate("/bills-inbox");
    } catch (e) { toast.error("Approve failed"); }
    finally { setApproving(false); }
  };

  if (!bill) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const isProcessed = bill.status === "processed";
  const isDuplicate = bill.status === "duplicate_review";

  const resolveDup = async (action) => {
    try {
      await api.post(`/bills/${id}/resolve-duplicate`, { action });
      if (action === "file_duplicate") {
        toast.success("Filed in Archive › Duplicates");
        navigate("/bills-inbox");
      } else {
        const r = await api.get(`/bills/${id}`);
        hydrate(r.data);
        toast.success(action === "revision" ? "Imported as a revised bill" : "Marked as not a duplicate");
      }
    } catch (e) { toast.error("Could not resolve"); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <button data-testid="back-to-inbox" onClick={() => navigate("/bills-inbox")} className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground">
        <ArrowLeft size={16} /> Bill Inbox
      </button>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Statement Review</p>
          <h1 className="font-display text-3xl font-black tracking-tighter mt-1 truncate max-w-xl">{bill.subject || bill.filename}</h1>
        </div>
        <Button data-testid="parse-ai-button" onClick={parse} disabled={parsing || isProcessed || isDuplicate} className="rounded-sm font-semibold transition-transform hover:-translate-y-[1px]">
          <Sparkle size={16} weight="fill" className={`mr-1 ${parsing ? "animate-pulse" : ""}`} />
          {parsing ? "Parsing…" : bill.parsed_lines?.length ? "Re-parse with AI" : "Parse with AI"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* PDF preview */}
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="p-3 border-b border-border flex items-center gap-2">
            <FilePdf size={16} weight="fill" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Original Document</span>
            <button data-testid="popout-pdf-button" onClick={popOut} className="ml-auto flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
              <ArrowSquareOut size={14} weight="bold" /> Pop out
            </button>
          </div>
          {pdfUrl ? (
            <iframe data-testid="pdf-preview" title="pdf" src={pdfUrl} className="w-full h-[640px] bg-secondary" />
          ) : (
            <div className="w-full h-[640px] bg-secondary flex items-center justify-center text-sm text-muted-foreground">Loading document…</div>
          )}
        </div>

        {/* Duplicate resolution panel */}
        {isDuplicate && (
          <div data-testid="duplicate-panel" className="bg-card border-2 border-destructive rounded-sm p-6 space-y-5">
            <div className="flex items-start gap-3">
              <WarningCircle size={28} weight="fill" className="text-destructive shrink-0" />
              <div>
                <h2 className="font-display text-xl font-black tracking-tight">Possible duplicate detected</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  This statement appears to match a document already in your system. Importing it again could double-count expenses in your ledger.
                </p>
                {bill.dedup_signature && (bill.dedup_signature.vendor_canonical || bill.dedup_signature.amount_due != null) && (
                  <div className="text-xs mt-2 font-mono bg-secondary rounded-sm px-2 py-1.5 inline-block space-y-0.5">
                    <div>Matched on: <span className="font-bold">{bill.dedup_signature.vendor_canonical || "vendor"}</span></div>
                    {bill.dedup_signature.amount_due != null && <div>Amount: ${Number(bill.dedup_signature.amount_due).toFixed(2)}</div>}
                    {bill.dedup_signature.coverage_start && <div>Coverage: {bill.dedup_signature.coverage_start} → {bill.dedup_signature.coverage_end || "?"}</div>}
                    {bill.dedup_signature.property_label && <div>Property: {bill.dedup_signature.property_label}</div>}
                    {bill.dedup_signature.account_number && <div>Account #: {bill.dedup_signature.account_number}</div>}
                  </div>
                )}
              </div>
            </div>

            {bill.duplicate_of && (
              <button
                data-testid="view-suspected-duplicate"
                onClick={() => navigate(`/bills-inbox/${bill.duplicate_of.id}`)}
                className="w-full text-left border border-border rounded-sm p-4 hover:bg-secondary transition-colors flex items-center gap-3"
              >
                <FilePdf size={22} weight="fill" className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Suspected duplicate of</p>
                  <p className="font-semibold truncate">{bill.duplicate_of.vendor || bill.duplicate_of.filename}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {bill.duplicate_of.filename}{bill.duplicate_of.statement_date ? ` · ${bill.duplicate_of.statement_date}` : ""} · status: {bill.duplicate_of.status}
                  </p>
                </div>
                <ArrowSquareOut size={18} className="text-muted-foreground shrink-0" />
              </button>
            )}

            <div className="space-y-2 pt-1">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Choose how to proceed</p>
              <Button data-testid="resolve-file-duplicate" onClick={() => resolveDup("file_duplicate")} variant="outline" className="w-full justify-start rounded-sm font-semibold h-auto py-3">
                <Archive size={18} className="mr-2 shrink-0" />
                <span className="text-left"><span className="block">Stop import — file in Duplicates</span><span className="block text-xs font-normal text-muted-foreground">Keep the document in Archive › Duplicates; no ledger entries.</span></span>
              </Button>
              <Button data-testid="resolve-revision" onClick={() => resolveDup("revision")} variant="outline" className="w-full justify-start rounded-sm font-semibold h-auto py-3">
                <FileArrowUp size={18} className="mr-2 shrink-0" />
                <span className="text-left"><span className="block">Import as a revised bill</span><span className="block text-xs font-normal text-muted-foreground">Process this as an updated version and continue.</span></span>
              </Button>
              <Button data-testid="resolve-not-duplicate" onClick={() => resolveDup("not_duplicate")} className="w-full justify-start rounded-sm font-semibold h-auto py-3">
                <CheckCircle size={18} weight="fill" className="mr-2 shrink-0" />
                <span className="text-left"><span className="block">Not a duplicate — proceed</span><span className="block text-xs font-normal opacity-80">Continue ingesting this statement normally.</span></span>
              </Button>
            </div>
          </div>
        )}

        {/* Extraction form */}
        <div className={`bg-card border border-border rounded-sm p-5 space-y-5 ${isDuplicate ? "hidden" : ""}`}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Vendor</label>
              <Input data-testid="bill-vendor-input" value={vendor} onChange={(e) => setVendor(e.target.value)} className="rounded-sm mt-1.5" disabled={isProcessed} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Statement Date</label>
              <Input data-testid="bill-date-input" type="date" value={stmtDate || ""} onChange={(e) => setStmtDate(e.target.value)} className="rounded-sm mt-1.5" disabled={isProcessed} />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{isOwnerStmt ? "Statement Total / Net (optional)" : "Total Payment (credit card)"}</label>
            <Input data-testid="bill-total-input" type="number" value={total} onChange={(e) => setTotal(e.target.value)} className="rounded-sm mt-1.5 font-mono" disabled={isProcessed} />
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Per-Property Lines</span>
            {!isProcessed && <button data-testid="add-line-button" onClick={addLine} className="text-xs font-semibold flex items-center gap-1 hover:underline"><Plus size={14} /> Add line</button>}
          </div>

          {lines.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-6 text-center text-sm text-muted-foreground">
              No line items yet. Click <span className="font-semibold">Parse with AI</span> to extract them automatically.
            </div>
          ) : (
            <div className="space-y-3">
              {lines.map((l, i) => (
                <div key={i} data-testid={`line-${i}`} className="border border-border rounded-sm p-3 space-y-2">
                  <div className="flex gap-2">
                    <Input data-testid={`line-desc-${i}`} value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder="Description" className="rounded-sm flex-1 h-9 text-sm" disabled={isProcessed} />
                    <Input data-testid={`line-amount-${i}`} type="number" value={l.amount} onChange={(e) => updateLine(i, { amount: e.target.value })} placeholder="0.00" className={`rounded-sm w-28 h-9 text-sm font-mono text-right ${lineType(l) === "income" ? "text-success" : ""}`} disabled={isProcessed} />
                    {!isProcessed && <button data-testid={`remove-line-${i}`} onClick={() => removeLine(i)} className="text-muted-foreground hover:text-destructive"><Trash size={16} /></button>}
                  </div>
                  <div className="flex gap-2">
                    <Select value={l.property_id || "none"} onValueChange={(v) => setLineProperty(i, v === "none" ? null : v)} disabled={isProcessed}>
                      <SelectTrigger data-testid={`line-property-${i}`} className={`rounded-sm h-9 text-sm flex-1 ${!l.property_id ? "border-destructive text-destructive" : ""}`}>
                        <SelectValue placeholder="⚠ Attribute to property" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unattributed</SelectItem>
                        {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={lineType(l)} onValueChange={(v) => updateLine(i, { type: v })} disabled={isProcessed}>
                      <SelectTrigger data-testid={`line-type-${i}`} className={`rounded-sm h-9 text-sm w-28 ${lineType(l) === "income" ? "text-success font-semibold" : ""}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={l.category || "other"} onValueChange={(v) => setCategory(i, v)} disabled={isProcessed}>
                      <SelectTrigger data-testid={`line-category-${i}`} className="rounded-sm h-9 text-sm w-44"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Unit attribution (multi-unit properties) */}
                  {l.property_id && (propUnits(l.property_id).length > 0 || (l.unit_guess && !l.unit_id)) && (
                    <div className="flex gap-2 items-center">
                      {propUnits(l.property_id).length > 0 && (
                        <Select value={l.unit_id || "none"} onValueChange={(v) => updateLine(i, { unit_id: v === "none" ? null : v })} disabled={isProcessed}>
                          <SelectTrigger data-testid={`line-unit-${i}`} className="rounded-sm h-9 text-sm w-52"><SelectValue placeholder="Whole property" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Whole property (no unit)</SelectItem>
                            {propUnits(l.property_id).map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                      {l.unit_guess && !l.unit_id && !isProcessed && (
                        <button
                          type="button"
                          data-testid={`add-unit-from-guess-${i}`}
                          onClick={() => openAddUnit(i)}
                          className="flex items-center gap-1.5 text-xs font-semibold text-foreground bg-secondary hover:bg-foreground hover:text-background transition-colors px-2 py-1.5 rounded-sm flex-1 justify-start"
                        >
                          <Buildings size={14} weight="fill" />
                          AI guess unit: "{l.unit_guess}"
                          <span className="ml-auto inline-flex items-center gap-1 opacity-80"><Plus size={12} /> Add unit</span>
                        </button>
                      )}
                    </div>
                  )}
                  {l.property_name_guess && !l.property_id && (
                    <button
                      type="button"
                      data-testid={`create-from-guess-${i}`}
                      onClick={() => openCreateFromGuess(i)}
                      disabled={isProcessed}
                      className="flex items-center gap-1.5 text-xs font-semibold text-foreground bg-secondary hover:bg-foreground hover:text-background transition-colors px-2 py-1.5 rounded-sm w-full justify-start"
                    >
                      <MagicWand size={14} weight="fill" />
                      AI guess: "{l.property_name_guess}"
                      <span className="ml-auto inline-flex items-center gap-1 opacity-80">
                        <Plus size={12} /> Create &amp; attribute
                      </span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Reconciliation */}
          {lines.length > 0 && (
            <div className="border-t border-border pt-4 space-y-2 text-sm">
              {isOwnerStmt ? (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">Income (rents, fees collected)</span><span className="font-mono tabular-nums font-semibold text-success">{fmtMoney(incomeTotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Expenses (mgmt fees, repairs…)</span><span className="font-mono tabular-nums font-semibold">{fmtMoney(expenseTotal)}</span></div>
                  <div className="flex items-center justify-between font-semibold border-t border-border pt-2">
                    <span>Net to owner</span>
                    <span data-testid="owner-net" className={`font-mono tabular-nums ${incomeTotal - expenseTotal >= 0 ? "text-success" : "text-destructive"}`}>{fmtMoney(incomeTotal - expenseTotal)}</span>
                  </div>
                  {bill?.statement_net != null && (
                    <div className="mt-3 pt-3 border-t border-dashed border-border">
                      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Reconcile to statement's printed totals</p>
                      {[["Income", incomeTotal, bill.statement_income_total], ["Expenses", expenseTotal, bill.statement_expense_total], ["Net", incomeTotal - expenseTotal, bill.statement_net]].map(([label, parsed, stmt]) => {
                        const has = stmt != null;
                        const diff = has ? Number(parsed) - Number(stmt) : 0;
                        const ok = has && Math.abs(diff) < 0.01;
                        return (
                          <div key={label} className="flex justify-between items-center text-xs py-0.5" data-testid={`reconcile-${label.toLowerCase()}`}>
                            <span className="text-muted-foreground">{label}</span>
                            <span className="flex items-center gap-3 font-mono tabular-nums">
                              <span>parsed {fmtMoney(parsed)}</span>
                              <span className="text-muted-foreground">stmt {has ? fmtMoney(stmt) : "—"}</span>
                              {has && (ok
                                ? <CheckCircle size={13} weight="fill" className="text-success" />
                                : <span className="text-destructive font-semibold" data-testid={`reconcile-diff-${label.toLowerCase()}`}>Δ {fmtMoney(diff)}</span>)}
                            </span>
                          </div>
                        );
                      })}
                      <p className="text-[10px] text-muted-foreground mt-1.5">A Δ usually means a pass-through item (owner draw, deposit, convenience fee) was mis-included — adjust the lines so parsed matches the statement.</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">Lines total</span><span className="font-mono tabular-nums font-semibold">{fmtMoney(expenseTotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Statement total</span><span className="font-mono tabular-nums">{fmtMoney(total)}</span></div>
                  <div className={`flex items-center justify-between font-semibold ${Math.abs(expenseTotal - Number(total)) < 0.01 ? "text-success" : "text-destructive"}`}>
                    <span className="flex items-center gap-1">
                      {Math.abs(expenseTotal - Number(total)) < 0.01 ? <CheckCircle size={16} weight="fill" /> : <WarningCircle size={16} weight="fill" />}
                      {Math.abs(expenseTotal - Number(total)) < 0.01 ? "Reconciled" : "Difference"}
                    </span>
                    <span className="font-mono tabular-nums">{fmtMoney(expenseTotal - Number(total))}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {isProcessed ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 bg-secondary text-foreground rounded-sm p-3 text-sm font-semibold">
                <CheckCircle size={18} weight="fill" className="text-success" /> Posted to ledger
              </div>
              <Button data-testid="restage-bill-button" variant="outline" onClick={restage} className="w-full rounded-sm font-semibold">
                <ArrowCounterClockwise size={16} className="mr-1" /> Restage &amp; remove ledger entries
              </Button>
            </div>
          ) : (
            <Button data-testid="approve-bill-button" onClick={approve} disabled={approving || lines.length === 0} className="w-full rounded-sm font-semibold h-11">
              {approving ? "Posting…" : unattributed > 0 ? `${unattributed} line(s) need a property` : "Approve & Post to Ledger"}
            </Button>
          )}
        </div>
      </div>

      {/* Create property from AI guess */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
              <MagicWand size={20} weight="fill" /> New Property from Statement
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            Pre-filled from the AI's reading of this statement. Confirm the details and we'll create the property and attribute this line to it.
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Name</Label>
              <Input data-testid="guess-property-name-input" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Type</Label>
              <Select value={createForm.property_type} onValueChange={(v) => setCreateForm({ ...createForm, property_type: v })}>
                <SelectTrigger data-testid="guess-property-type-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PROPERTY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Address</Label>
              <Input data-testid="guess-property-address-input" value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} className="rounded-sm mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">City</Label>
                <Input data-testid="guess-property-city-input" value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} className="rounded-sm mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">State</Label>
                <Input data-testid="guess-property-state-input" value={createForm.state} onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })} className="rounded-sm mt-1.5" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="create-attribute-button" onClick={createPropertyAndAttribute} disabled={creating} className="rounded-sm font-semibold w-full">
              {creating ? "Creating…" : "Create Property & Attribute Line"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Unit from AI guess */}
      <Dialog open={unitOpen} onOpenChange={setUnitOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
              <Buildings size={20} weight="fill" /> Add Unit from Statement
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            The statement references a unit that isn't set up yet. Confirm the property and unit label, then we'll create the unit and attribute this line to it.
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Property</Label>
              <Select value={unitForm.property_id} onValueChange={(v) => setUnitForm({ ...unitForm, property_id: v })}>
                <SelectTrigger data-testid="add-unit-property-select" className="rounded-sm mt-1.5"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Unit Label / Number</Label>
              <Input data-testid="add-unit-label-input" value={unitForm.label} onChange={(e) => setUnitForm({ ...unitForm, label: e.target.value })} placeholder="e.g. Unit 15 / Apt B" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Monthly Rent (optional)</Label>
              <Input data-testid="add-unit-rent-input" type="number" value={unitForm.monthly_rent} onChange={(e) => setUnitForm({ ...unitForm, monthly_rent: e.target.value })} className="rounded-sm mt-1.5 font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="create-unit-attribute-button" onClick={createUnitAndAttribute} disabled={creatingUnit} className="rounded-sm font-semibold w-full">
              {creatingUnit ? "Adding…" : "Add Unit & Attribute Line"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
